import * as React from 'react';
import i18n from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import { turnDrawerOff } from 'store/app/actions';
import { getDrawerData, getDrawerOpen, getModalOpen } from 'store/app/selectors';
import { resetPwaSheetDragState } from 'utils/usePwaBottomSheetDragClose';
import { AppDrawer } from '@monorepo/shared-lib';
import { DrawerType } from 'store/app/enums';
import { isPwaTaxReturnCreationAllowed } from 'utils/pwa-nav';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { useOverlayBackHandler } from 'utils/useOverlayBackHandler';

const IntegratedDrawerToolbarContext = React.createContext<{
  setToolbar: (node: React.ReactNode | null) => void;
} | null>(null);

const IntegratedDrawerTitleExtraContext = React.createContext<{
  setTitleExtra: (node: React.ReactNode | null) => void;
} | null>(null);

/**
 * Register extra controls in the drawer header (between title and close) when
 * `integratedHeader` is set on DrawerData — e.g. layout toggles + Save/Cancel for transaction review.
 */
export function useIntegratedDrawerToolbar(
  enabled: boolean,
  toolbar: React.ReactNode | null
): void {
  const ctx = React.useContext(IntegratedDrawerToolbarContext);
  const setToolbar = ctx?.setToolbar;
  React.useEffect(() => {
    if (!setToolbar) return;
    if (!enabled || toolbar == null) {
      setToolbar(null);
      return;
    }
    setToolbar(toolbar);
    return () => {
      setToolbar(null);
    };
  }, [enabled, toolbar, setToolbar]);
}

/** Register controls beside the integrated drawer title (e.g. edit mode toggle). */
export function useIntegratedDrawerTitleExtra(
  enabled: boolean,
  titleExtra: React.ReactNode | null
): void {
  const ctx = React.useContext(IntegratedDrawerTitleExtraContext);
  const setTitleExtra = ctx?.setTitleExtra;
  React.useEffect(() => {
    if (!setTitleExtra) return;
    if (!enabled || titleExtra == null) {
      setTitleExtra(null);
      return;
    }
    setTitleExtra(titleExtra);
    return () => {
      setTitleExtra(null);
    };
  }, [enabled, titleExtra, setTitleExtra]);
}

class DrawerContentErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  state = { hasError: false, error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Drawer content error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <div
          style={{
            padding: 24,
            color: 'var(--color-text-dark, #042a2a)',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          <p style={{ fontWeight: 600, marginBottom: 8 }}>
            {i18n.t('other.something-went-wrong')}
          </p>
          <p style={{ fontSize: 13, color: 'var(--color-text-muted, #5a6a6a)', marginBottom: 16 }}>
            {this.state.error.message}
          </p>
          <pre
            style={{
              fontSize: 11,
              overflow: 'auto',
              background: '#f5f5f5',
              padding: 12,
              borderRadius: 4,
            }}
          >
            {this.state.error.stack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

export const DrawerContainer: React.FC = () => {
  const dispatch = useDispatch();
  const drawerData = useSelector(getDrawerData);
  const drawerOpen = useSelector(getDrawerOpen);
  const modalOpen = useSelector(getModalOpen);
  const prevModalOpenRef = React.useRef(modalOpen);

  const [integratedToolbarSlot, setIntegratedToolbarSlot] =
    React.useState<React.ReactNode | null>(null);
  const [integratedTitleExtraSlot, setIntegratedTitleExtraSlot] =
    React.useState<React.ReactNode | null>(null);

  const integratedToolbarApi = React.useMemo(
    () => ({ setToolbar: setIntegratedToolbarSlot }),
    []
  );
  const integratedTitleExtraApi = React.useMemo(
    () => ({ setTitleExtra: setIntegratedTitleExtraSlot }),
    []
  );

  React.useEffect(() => {
    if (!drawerOpen) {
      setIntegratedToolbarSlot(null);
      setIntegratedTitleExtraSlot(null);
    }
  }, [drawerOpen]);

  React.useEffect(() => {
    if (prevModalOpenRef.current && !modalOpen && drawerOpen) {
      resetPwaSheetDragState();
    }
    prevModalOpenRef.current = modalOpen;
  }, [drawerOpen, modalOpen]);

  React.useEffect(() => {
    if (!drawerData?.integratedHeader) {
      setIntegratedToolbarSlot(null);
      setIntegratedTitleExtraSlot(null);
    }
  }, [drawerData?.integratedHeader]);

  // PWA: block create tax return / create submission drawers (view & edit only).
  React.useEffect(() => {
    if (!drawerOpen || !drawerData) return;
    if (isPwaTaxReturnCreationAllowed()) return;
    if (
      drawerData.type === DrawerType.ADD_TAX_SUBMISSION ||
      drawerData.type === DrawerType.ADD_TAX_RETURN
    ) {
      dispatch(turnDrawerOff());
    }
  }, [drawerOpen, drawerData, dispatch]);

  const close = React.useCallback(() => {
    resetPwaSheetDragState();
    dispatch(turnDrawerOff());
  }, [dispatch]);
  const drawerBodyKey =
    drawerData?.contentKey != null &&
    String(drawerData.contentKey).trim() !== ''
      ? `${drawerData.type}-${drawerData.contentKey}`
      : String(drawerData?.type ?? 'drawer');
  const requestedWidth = drawerData?.width ?? (drawerData?.wide ? 800 : 400);
  const isIntegrated = Boolean(drawerData?.integratedHeader);
  const isRightsDrawer = drawerData?.type === DrawerType.RIGHTS;
  const body = drawerData?.component ?? null;
  const pwaMobileNav = usePwaMobileNavEnabled();
  const blockCreateDrawer =
    pwaMobileNav &&
    (drawerData?.type === DrawerType.ADD_TAX_SUBMISSION ||
      drawerData?.type === DrawerType.ADD_TAX_RETURN);
  useOverlayBackHandler(
    Boolean(drawerOpen && !blockCreateDrawer),
    close,
    `drawer:${drawerBodyKey}`,
    pwaMobileNav
  );
  // On PWA, integrated drawers use custom in-body chrome (bottom-sheet style),
  // so always hide AppDrawer's integrated title row.
  const hideIntegratedTitle = Boolean(pwaMobileNav && isIntegrated);
  /** Access Rights needs a tall sheet — entity list + permission chips. */
  const pwaTallSheet = Boolean(pwaMobileNav && (isIntegrated || isRightsDrawer));
  const showBottomSheetHandle = !(
    pwaMobileNav && drawerData?.bodyProvidesBottomSheetHandle
  );
  const pwaRootClassName = pwaMobileNav
    ? [
        'scope-pwa-drawer-root',
        pwaTallSheet ? 'scope-pwa-drawer-root--tall' : undefined,
        isRightsDrawer ? 'scope-pwa-drawer-root--user-rights' : undefined,
      ]
        .filter(Boolean)
        .join(' ')
    : undefined;

  return (
    <AppDrawer
      open={drawerOpen && !blockCreateDrawer}
      onClose={close}
      title={drawerData?.title}
      subtitle={drawerData?.subtitle}
      width={requestedWidth}
      closeAriaLabel={String(i18n.t('common.close'))}
      maskClosable={
        // PWA bottom sheets: always allow tapping the dimmed area above to dismiss
        // (unless a modal is stacked on top). Desktop keeps per-drawer settings.
        pwaMobileNav
          ? !modalOpen
          : (drawerData?.maskClosable ?? true) && !modalOpen
      }
      hideCloseButton={
        drawerData?.hideCloseButton === true || hideIntegratedTitle
      }
      contentKey={drawerBodyKey}
      contentPadding={drawerData?.drawerContentPadding}
      integrated={isIntegrated}
      titleExtra={integratedTitleExtraSlot ?? drawerData?.integratedTitleExtra}
      toolbar={integratedToolbarSlot}
      fullViewportWidth={pwaMobileNav}
      placement={pwaMobileNav ? 'bottom' : 'right'}
      height={pwaMobileNav ? (pwaTallSheet ? '92vh' : 'auto') : undefined}
      // Above PWA bottom nav (1200) so mask + close receive taps.
      zIndex={pwaMobileNav ? 1250 : 1200}
      className={pwaMobileNav ? 'scope-pwa-drawer' : undefined}
      rootClassName={pwaRootClassName}
      contentClassName={
        [
          pwaMobileNav ? 'scope-pwa-drawer-content' : undefined,
          pwaMobileNav && isRightsDrawer
            ? 'scope-pwa-user-rights-drawer'
            : undefined,
        ]
          .filter(Boolean)
          .join(' ') || undefined
      }
      hideIntegratedTitle={hideIntegratedTitle}
      showBottomSheetHandle={showBottomSheetHandle}
      toolbarClassName={
        hideIntegratedTitle ? 'drawer-header-toolbar-slot--full' : undefined
      }
    >
      <DrawerContentErrorBoundary>
        {isIntegrated ? (
          <IntegratedDrawerToolbarContext.Provider
            value={integratedToolbarApi}
          >
            <IntegratedDrawerTitleExtraContext.Provider
              value={integratedTitleExtraApi}
            >
              {body}
            </IntegratedDrawerTitleExtraContext.Provider>
          </IntegratedDrawerToolbarContext.Provider>
        ) : (
          body
        )}
      </DrawerContentErrorBoundary>
    </AppDrawer>
  );
};
